import {
  HONEYDEW_CONTENT_STORAGE,
  HONEYDEW_MANIFEST_PATH,
  honeydewFileFields,
  parseHoneydewManifest,
  withHoneydewContents,
  type HoneydewStoredFile,
} from '../domain/honeydewResult';
import type { DataFileToDownload } from './dataFileUploads';
import type { Task } from '../types';

async function downloadStoredFile(file: DataFileToDownload, maxBytes?: number): Promise<Uint8Array> {
  const { downloadDataFileBytes } = await import('./dataFileUploads');
  return downloadDataFileBytes(file, maxBytes);
}

/** A subscription owns its cache, so navigating away releases the loaded text. */
export function createHoneydewTaskLoader(
  download = downloadStoredFile,
) {
  let cachedKey = '';
  let cachedContents: Promise<{ contents: Record<string, string>; files: HoneydewStoredFile[] }> | undefined;
  return async (task: Task): Promise<Task> => {
    const result = task.result;
    if (task.taskType !== 'comp-workflows-scientific'
      || result?.contentStorage !== HONEYDEW_CONTENT_STORAGE) return task;

    const fields = honeydewFileFields(result);
    const manifest = result.submissionManifest as HoneydewStoredFile | undefined;
    const inlineFiles = (Array.isArray(result.submissionFiles) ? result.submissionFiles : []) as HoneydewStoredFile[];
    const key = JSON.stringify([task.id, result.uploadedByUid, fields, manifest ?? inlineFiles]);
    if (key !== cachedKey || !cachedContents) {
      cachedKey = key;
      cachedContents = (async () => {
        const contents: Record<string, string> = {};
        let totalBytes = 0;
        const validatePath = (file: HoneydewStoredFile) => {
          const prefix = `dataFiles/${result.uploadedByUid}/${task.id}/`;
          const relative = typeof file.storagePath === 'string' ? file.storagePath.slice(prefix.length) : '';
          if (typeof file.storagePath !== 'string' || !file.storagePath.startsWith(prefix)
            || relative.split('/').slice(1).join('/') !== file.path
            || relative.split('/').some((part) => !part || part === '.' || part === '..')) {
            throw new Error(`Invalid submission storage path: ${file.path}.`);
          }
        };
        const read = async (file: HoneydewStoredFile) => {
          validatePath(file);
          totalBytes += file.sizeBytes;
          if (!Number.isSafeInteger(file.sizeBytes) || file.sizeBytes < 0
            || totalBytes > 64 * 1024 * 1024) {
            throw new Error('Submission text files exceed the 64 MiB loading limit.');
          }
          const bytes = await download(file, file.sizeBytes + 1);
          const digest = await crypto.subtle.digest('SHA-256', new Uint8Array(bytes));
          const sha256 = Array.from(new Uint8Array(digest), (byte) => byte.toString(16).padStart(2, '0')).join('');
          if (bytes.byteLength !== file.sizeBytes || sha256 !== file.sha256) {
            throw new Error(`Submission file changed in Storage: ${file.path}. Re-upload the bundle.`);
          }
          return new TextDecoder().decode(bytes);
        };
        let files = inlineFiles;
        if (manifest) {
          if (manifest.path !== HONEYDEW_MANIFEST_PATH) throw new Error('Invalid submission manifest path.');
          files = parseHoneydewManifest(await read(manifest));
          const sessionPrefix = manifest.storagePath.slice(0, -manifest.path.length);
          for (const file of files) {
            validatePath(file);
            if (file.storagePath !== `${sessionPrefix}${file.path}` || file.path === HONEYDEW_MANIFEST_PATH) {
              throw new Error('Submission manifest contains a file outside its upload session.');
            }
          }
        }
        // Load selected text files only, not the thousands of data/dependency files.
        for (const [field, path] of Object.entries(fields)) {
          const file = files.find((entry) => entry.path === path);
          if (file) contents[field] = await read(file);
        }
        return { contents, files };
      })();
    }
    const pending = cachedContents;
    try {
      const loaded = await pending;
      return {
        ...task,
        result: withHoneydewContents({
          ...result,
          ...(manifest ? {
            submissionFiles: loaded.files,
            submissionFileList: loaded.files.map((file) => file.path),
          } : {}),
        }, loaded.contents),
      };
    } catch (error) {
      if (cachedContents === pending) cachedContents = undefined;
      throw error;
    }
  };
}

export async function loadTaskFileContents(task: Task): Promise<Task> {
  return createHoneydewTaskLoader()(task);
}
